import Splide from "@splidejs/splide";

declare global {
    interface Window {
        employeesData: {
            id: number;
            name: string;
            role: string;
            categories: string[];
            thumbnail: string | null;
            permalink: string;
        }[];
    }
}

class Team {
    private readonly splide: HTMLElement;
    private buttons: NodeListOf<HTMLButtonElement>;
    private data: any[];
    private title: HTMLElement;

    constructor(private component: HTMLElement) {
        this.splide = this.component.querySelector<HTMLElement>(".team-splide");
        this.title = this.component.querySelector<HTMLElement>('.archive-title');
        this.data = (window as any).employeesData ?? [];
        this.buttons = this.component.querySelectorAll<HTMLButtonElement>(".archive-filter button.archive-filter-item");
        this.init();
    }

    private init() {
        const splideList = this.splide.querySelector<HTMLElement>('.splide__list');

        this.data.forEach(employee => {
            const slide = this.createSlide(employee);
            splideList.appendChild(slide);
        });

        const splide = new Splide(this.splide, {
                type: 'slide',
                gap: '60px',
                perPage: 3,
                perMove: 1,
                arrows: true,
                pagination: true,
                slideFocus: false,
                breakpoints: {
                    1024: {
                        perPage: 2,
                    },
                    767: {
                        perPage: 1,
                    }
                }
            }
        );

        const updateInertAttributes = () => {
            const slides = this.splide.querySelectorAll<HTMLElement>('.splide__slide');

            slides?.forEach(slide => {
                const isHidden = slide.getAttribute('aria-hidden') === 'true';
                if (isHidden) {
                    slide.setAttribute('inert', '');
                    slide.querySelectorAll('a, button').forEach(el => el.setAttribute('tabindex', '-1'));
                } else {
                    slide.removeAttribute('inert');
                    slide.querySelectorAll('a, button').forEach(el => el.removeAttribute('tabindex'));
                }
            });
        };

        splide.on('drag', () => {
            if (document.activeElement && this.splide.contains(document.activeElement)) {
                (document.activeElement as HTMLElement).blur();
            }
        });

        splide.on('visible', updateInertAttributes);

        splide.on('mounted', () => {
            this.buttons.forEach(button => {
                button.addEventListener('click', () => {
                    if (button.classList.contains('active')) return;

                    splide.destroy();
                    const category = button.getAttribute('data-category');

                    this.buttons.forEach(btn => btn.classList.remove('active'));
                    button.classList.add('active');

                    splide.Components.Slides.forEach(slide => slide.destroy());
                    splideList.innerHTML = '';

                    this.data.forEach(employee => {
                        if (category === 'all' || (employee.categories && employee.categories.includes(category))) {
                            splideList.appendChild(this.createSlide(employee));
                        }
                    });

                    this.title.textContent = category === 'all' ? 'Hele team' : button.textContent;

                    splide.mount();
                    updateInertAttributes();
                });
            });
        });

        splide.mount();
    }

    private createSlide(employee: {
        permalink: string;
        name: string;
        role: string;
        thumbnail: string;
        categories: string[]
    }): HTMLElement {
        const slide = document.createElement('li');
        const categoriesString = (employee.categories && employee.categories.length > 0) ? employee.categories.join(' ') : '';

        slide.className = 'splide__slide team-member';
        slide.setAttribute('data-categories', categoriesString);

        const first_name = employee.name.split(' ')[0];
        const rest_name = employee.name.split(' ').slice(1).join(' ');

        slide.innerHTML = `
            <a href="${employee.permalink}" class="team-member-card">
                <div class="team-member-card-image">
                    <img src="${employee.thumbnail}" alt="${employee.name}">
                </div>
                
                <div class="team-member-card-label">
                    <h3 class="team-member-card-name">${first_name}<br>${rest_name}</h3>
                    <p class="team-member-card-position">${employee.role}</p>
                </div>
            </a>
        `;
        return slide;
    }
}

window.addEventListener('load', () => {
    const teamArchive = document.querySelector<HTMLElement>('.team-archive');

    if (teamArchive) {
        new Team(teamArchive);
    }
});